import React, { useMemo } from 'react'

import { FeedbacksActiveCount } from '@/entities/feedbacks'
import {
  $sessionIsAuthenticated,
  $sessionPending,
  $sessionRole,
  logoutRequested,
} from '@/shared/api'
import { ROUTES } from '@/shared/config'
import { ErrorResult } from '@/shared/ui'
import { LogoutOutlined } from '@ant-design/icons'
import { type MenuProps, Spin } from 'antd'
import { useUnit } from 'effector-react'
import { Link, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { MainLayout } from '../layouts'
import { $pageHeaderTabs, $routes } from './routes'
import styles from './styles.module.scss'
import { type IRoute, type ITab } from './types'

export type MenuItem = Required<MenuProps>['items'][number] & {
  path?: string
  children?: MenuItem[]
}

type PrivateRouteProps = {
  route?: IRoute
}
function PrivateRoute({ route }: PrivateRouteProps) {
  const isAuthenticated = useUnit($sessionIsAuthenticated)
  const role = useUnit($sessionRole)

  if (!isAuthenticated || !role) {
    return (
      <ErrorResult
        customConfig={{
          status: '403',
          title: 'Доступ запрещён',
          customAction: {
            label: 'Авторизация',
            onClick: () => logoutRequested(),
          },
        }}
      />
    )
  }

  if (!route) return null

  if (route.roles && !route.roles.includes(role)) {
    return (
      <ErrorResult
        customConfig={{
          status: '403',
          title: 'Доступ запрещён',
        }}
      />
    )
  }

  return <>{route.component}</>
}

// ---- LayoutWrapper ----
type LayoutWrapperProps = { children: React.ReactNode }
function LayoutWrapper({ children }: LayoutWrapperProps) {
  const requestLogout = useUnit(logoutRequested)
  const { pathname } = useLocation()
  const isAuthenticated = useUnit($sessionIsAuthenticated)
  const role = useUnit($sessionRole)
  const pageHeaderTabs = useUnit($pageHeaderTabs)

  const menuItems: MenuItem[] = useMemo(() => {
    const convertTabsToMenuItems = (tabs: ITab[]): MenuItem[] =>
      tabs.map((tab, index) => {
        const path = typeof tab.path === 'string' ? tab.path : ''
        const key = path || `key-${index}`

        const children = tab.children
          ? convertTabsToMenuItems(tab.children)
          : undefined

        let extra: React.ReactNode | undefined
        if (tab.path === ROUTES.FEEDBACK.ROOT) {
          extra = <FeedbacksActiveCount />
        }

        return {
          path,
          key,
          icon: tab.icon && React.createElement(tab.icon),
          label: path ? <Link to={path}>{tab.text}</Link> : tab.text,
          children,
          extra,
        }
      })

    const items = convertTabsToMenuItems(pageHeaderTabs)

    const logoutItem: MenuItem = {
      key: 'logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: () => requestLogout(),
      label: 'Выход',
    }

    items.push(logoutItem)

    return items
  }, [pageHeaderTabs, requestLogout])

  const findSelectedKey = (
    items: MenuItem[],
    currentPath: string,
  ): string | undefined => {
    for (const item of items) {
      if (item.path && currentPath.startsWith(item.path)) {
        return item.path
      }
      if (item.children) {
        const childSelectedKey = findSelectedKey(item.children, currentPath)
        if (childSelectedKey) return childSelectedKey
      }
    }
    return undefined
  }

  const selectedKey = findSelectedKey(menuItems, pathname)

  if (isAuthenticated && role) {
    return (
      <MainLayout
        selectedMenuKeys={selectedKey ? [selectedKey] : []}
        menuItems={menuItems}
      >
        {children}
      </MainLayout>
    )
  }

  return <>{children}</>
}

export const Router = () => {
  const routes = useUnit($routes)
  const isAuthenticated = useUnit($sessionIsAuthenticated)
  const role = useUnit($sessionRole)
  const sessionPending = useUnit($sessionPending)

  if (sessionPending) {
    return (
      <div className={styles.fullScreenCenter}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Routes>
      <Route
        element={
          <LayoutWrapper>
            <Outlet />
          </LayoutWrapper>
        }
      >
        {routes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<PrivateRoute route={route} />}
          />
        ))}

        <Route
          path={ROUTES.ANY}
          element={
            isAuthenticated && role ? (
              <ErrorResult
                customConfig={{ status: '404' }}
                notFoundConfig={{
                  navigateTo: ROUTES.MAIN.ROOT,
                  buttonLabel: 'На главную',
                }}
              />
            ) : (
              <PrivateRoute />
            )
          }
        />
      </Route>
    </Routes>
  )
}
